import { type FC, type ReactElement, type ReactNode, useEffect, useState } from 'react'
import { Box, Link, Stack, Typography, useTheme } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { Button } from '@dhl-ui/components'
import { Cookie, Info } from '@dhl/icons'
import { Language } from '@dhl/i18n'
import { Cookies } from '@dhl/illustrations'
import { useLegalLinks } from '@dhl/legal'
import { NativeModal } from '@dhl/native-modal'
import { shouldShowBanner, showPreferenceCentre, useOneTrustContext } from '@dhl/onetrust'

type ContentRowProps = {
  body: ReactNode
  icon: ReactElement
}

const ContentRow: FC<ContentRowProps> = ({ body, icon }: ContentRowProps) => (
  <Stack
    flexDirection="row"
    justifyContent="space-between"
    sx={{
      backgroundColor: (theme) => theme.palette.grey[200],
      borderRadius: '3px',
      padding: 2,
      gap: 1,
    }}
  >
    <Box>{icon}</Box>
    <Stack flexDirection="column" flexGrow={1} mt={0.375} gap={1}>
      {body && <Typography>{body}</Typography>}
    </Stack>
  </Stack>
)

export const NativeTracking: FC = () => {
  const {
    t,
    i18n: {
      options: { supportedLngs = [] },
    },
  } = useTranslation()
  const {
    palette: { mode },
  } = useTheme()

  const { privacy } = useLegalLinks()
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined)
  const { isOneTrustLoaded, nonce } = useOneTrustContext()

  const supportedLanguages = Array.isArray(supportedLngs)
    ? supportedLngs.filter((lang) => /^[a-z]{2}-[A-Z]{2}$/.test(lang))
    : []

  useEffect(() => {
    const showOneTrustBanner = shouldShowBanner()

    if (showOneTrustBanner) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [])

  useEffect(() => {
    if (!isOneTrustLoaded) {
      return
    }

    window.OneTrust?.OnConsentChanged?.(() => {
      setIsOpen(false)
    })
  }, [isOneTrustLoaded])

  const handleAgreeAllClick = () => document.getElementById('onetrust-accept-btn-handler')?.click()
  const handleStrictNecessaryClick = () => document.getElementById('onetrust-reject-all-handler')?.click()
  const handleSetupClick = () => showPreferenceCentre({ nonce, enableDarkModeStyle: mode === 'dark' })

  const handleClose = () => setIsOpen(undefined)

  if (isOpen === undefined || !isOneTrustLoaded) {
    return null
  }

  return (
    <NativeModal isCloseable={false} isSwipeable={false} open={isOpen} onClose={handleClose}>
      <NativeModal.Header isCloseable={false}>
        <Box display="flex">
          <Box ml="auto">
            <Language locales={supportedLanguages} />
          </Box>
        </Box>
      </NativeModal.Header>
      <NativeModal.Content>
        <Box>
          <Stack alignItems="center">
            <Cookies sx={{ width: '100%', height: 'auto', maxHeight: 250 }} />
            <Box mt={3} mb={1} textAlign="center">
              <Typography variant="h4">{t('thirdParty.oneTrustDialog.title')}</Typography>
            </Box>
          </Stack>

          <Stack gap={2} sx={{ mt: 1 }}>
            <ContentRow body={t('thirdParty.oneTrustDialog.content1')} icon={<Cookie />} />

            <ContentRow
              body={
                <Trans
                  i18nKey="thirdParty.oneTrustDialog.content2"
                  components={{
                    privacyLink: <Link href={privacy.url} rel="noreferrer" target="_blank" />,
                  }}
                />
              }
              icon={<Info />}
            />
          </Stack>
        </Box>

        <Stack gap={2} sx={{ padding: ({ spacing }) => spacing(3, 2, 3, 2) }}>
          <Button fullWidth variant="contained" onClick={handleSetupClick}>
            {t('thirdParty.oneTrustDialog.buttonSetup')}
          </Button>

          <Button fullWidth variant="contained" onClick={handleStrictNecessaryClick}>
            {t('thirdParty.oneTrustDialog.buttonStrictNecessary')}
          </Button>

          <Button fullWidth variant="contained" onClick={handleAgreeAllClick}>
            {t('thirdParty.oneTrustDialog.buttonAllowAll')}
          </Button>
        </Stack>
      </NativeModal.Content>
    </NativeModal>
  )
}
